import { useEffect, useMemo, useState } from 'react';
import { TFunction } from 'i18next';
import cx from 'classnames';
import {
    Control,
    Controller,
    FieldErrors,
    UseFormRegister,
    useFieldArray,
    useWatch,
} from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import Input from 'src/components/FormComponents/Input';
import { MultiSelectList } from 'src/components/FormComponents/MultiSelectList';
import { buildGroupOptionTree } from 'src/components/FormComponents/groupOptionTree';
import { OrganizationRolePermission } from 'src/domain/organization';
import {
    OrganizationFormValues,
    OrganizationGroupFormValue,
} from './createOrganizationForm.schema';
import { TextIcon } from 'src/components/Icons/TextIcon';
import { CaretIcon, PlusIcon } from 'src/components/Icons/icons';

interface RolesSectionProps {
    control: Control<OrganizationFormValues>;
    register: UseFormRegister<OrganizationFormValues>;
    errors: FieldErrors<OrganizationFormValues>;
    t: TFunction;
    groups: OrganizationGroupFormValue[];
    leafLabelSuffixByGroupId?: Record<string, string>;
    readOnly?: boolean;
}

export const OrganizationRolesSection = ({
    control,
    register,
    errors,
    t,
    groups,
    leafLabelSuffixByGroupId,
    readOnly = false,
}: RolesSectionProps) => {
    const { fields, append, remove, update } = useFieldArray({
        control,
        name: 'roles',
    });

    const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set());
    const toggleRole = (roleId: string) => {
        setExpandedRoles(prev => {
            const next = new Set(prev);
            next.has(roleId) ? next.delete(roleId) : next.add(roleId);
            return next;
        });
    };

    const handleAddRole = () => {
        const id = uuidv4();
        append({
            id,
            name: '',
            groupIds: [],
            permissions: [OrganizationRolePermission.readResidents],
        });
        setExpandedRoles(prev => new Set(prev).add(id));
    };

    const availableGroups = (groups ?? []).filter(
        group => group.name?.trim().length,
    );

    const watchedRoles = useWatch({ control, name: 'roles' });

    useEffect(() => {
        if (!watchedRoles) return;

        const validGroupIds = new Set((groups ?? []).map(group => group.id));
        const validPermissions = new Set(
            Object.values(OrganizationRolePermission),
        );
        const requiredPermission = OrganizationRolePermission.readResidents;

        watchedRoles.forEach((role, index) => {
            const filteredGroupIds = (role?.groupIds ?? []).filter(id =>
                validGroupIds.has(id),
            );

            const filteredPermissions = (role?.permissions ?? []).filter(
                permission => validPermissions.has(permission),
            );
            const normalizedPermissions = [
                requiredPermission,
                ...filteredPermissions.filter(
                    permission => permission !== requiredPermission,
                ),
            ];

            const hasGroupChanges =
                filteredGroupIds.length !== (role?.groupIds ?? []).length;
            const hasPermissionChanges =
                normalizedPermissions.length !==
                    (role?.permissions ?? []).length ||
                normalizedPermissions.some(
                    (permission, permissionIndex) =>
                        permission !==
                        (role?.permissions ?? [])[permissionIndex],
                );

            if (hasGroupChanges || hasPermissionChanges) {
                update(index, {
                    ...role,
                    groupIds: filteredGroupIds,
                    permissions: normalizedPermissions,
                });
            }
        });
    }, [groups, update, watchedRoles]);

    const groupOptions = useMemo(
        () =>
            buildGroupOptionTree(
                availableGroups,
                leafLabelSuffixByGroupId
                    ? {
                          getLabel: (group, isLeaf) => {
                              const baseLabel = group.name?.trim().length
                                  ? group.name
                                  : group.id;

                              if (!isLeaf) return baseLabel;

                              const suffix = leafLabelSuffixByGroupId[group.id];
                              return suffix
                                  ? `${baseLabel} - ${suffix}`
                                  : baseLabel;
                          },
                      }
                    : undefined,
            ),
        [availableGroups, leafLabelSuffixByGroupId],
    );

    const permissionOptions = useMemo(
        () => [
            {
                label: t(
                    'admin.forms.createOrganization.rolePermissionsReadResidents',
                ),
                value: OrganizationRolePermission.readResidents,
                disabled: true,
            },
            {
                label: t(
                    'admin.forms.createOrganization.rolePermissionsWriteResidents',
                ),
                value: OrganizationRolePermission.writeResidents,
            },
            {
                label: t(
                    'admin.forms.createOrganization.rolePermissionsReadEmployees',
                ),
                value: OrganizationRolePermission.readEmployees,
            },
            {
                label: t(
                    'admin.forms.createOrganization.rolePermissionsWriteEmployees',
                ),
                value: OrganizationRolePermission.writeEmployees,
            },
            {
                label: t(
                    'admin.forms.createOrganization.rolePermissionsSendMessagesToAll',
                ),
                value: OrganizationRolePermission.sendMessagesToAll,
            },
        ],
        [t],
    );

    return (
        <div className="space-y-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <h3 className="text-lg font-semibold">
                    {t('admin.forms.createOrganization.rolesTitle')}
                </h3>
                {!readOnly ? (
                    <button
                        type="button"
                        className="primary-btn-small flex items-center gap-1 pl-2 pr-4 py-1"
                        onClick={handleAddRole}
                    >
                        <TextIcon SVG={PlusIcon} />
                        <span>
                            {t('admin.forms.createOrganization.addRole')}
                        </span>
                    </button>
                ) : null}
            </div>
            {fields.length === 0 && (
                <p className="text-sm text-mongooseGray">
                    {t('admin.forms.createOrganization.rolesEmpty')}
                </p>
            )}
            {fields.map((field, index) => {
                const role = watchedRoles?.[index];
                const roleId = role?.id ?? field.id;
                const roleName =
                    role?.name?.trim() ||
                    t('admin.forms.createOrganization.roleName');
                const groupCount = role?.groupIds?.length ?? 0;
                const permissionCount = role?.permissions?.length ?? 0;
                const isExpanded = expandedRoles.has(roleId);
                const roleGroupIdsError = errors.roles?.[index]?.groupIds;
                const normalizedGroupIdsError = Array.isArray(roleGroupIdsError)
                    ? roleGroupIdsError[0]
                    : roleGroupIdsError;
                const rolePermissionsError = errors.roles?.[index]?.permissions;
                const normalizedPermissionsError = Array.isArray(
                    rolePermissionsError,
                )
                    ? rolePermissionsError[0]
                    : rolePermissionsError;

                return (
                    <div
                        key={field.id}
                        className="rounded-2xl border border-starkWhite bg-white shadow-sm"
                    >
                        <button
                            type="button"
                            onClick={() => toggleRole(roleId)}
                            className="flex w-full items-center gap-3 p-4 text-left"
                        >
                            <span className="flex-1 font-medium text-woodyBrown truncate">
                                {roleName}
                            </span>
                            <span className="text-xs text-mongooseGray whitespace-nowrap">
                                {groupCount}{' '}
                                {t('admin.forms.createOrganization.roleGroups')}
                            </span>
                            <span className="text-xs text-mongooseGray whitespace-nowrap">
                                {permissionCount}{' '}
                                {t(
                                    'admin.forms.createOrganization.rolePermissions',
                                )}
                            </span>
                            <div
                                className={cx(
                                    'transition-transform duration-200 text-woodyBrown text-opacity-50',
                                    isExpanded ? 'rotate-180' : 'rotate-0',
                                )}
                            >
                                <TextIcon SVG={CaretIcon} />
                            </div>
                        </button>

                        <div
                            className={cx(
                                'overflow-hidden transition-all duration-300',
                                isExpanded
                                    ? 'max-h-[5000px] opacity-100'
                                    : 'max-h-0 opacity-0',
                            )}
                        >
                            <div className="px-4 pb-4 space-y-3">
                                <div className="flex gap-3">
                                    {/* <Input
                                        className="text-input"
                                        label={t(
                                            'admin.forms.createOrganization.roleId',
                                        )}
                                        disabled
                                        error={errors.roles?.[index]?.id}
                                        {...register(`roles.${index}.id` as const)}
                                    /> */}
                                    <Input
                                        className="text-input"
                                        containerClassName="flex-1"
                                        label={t(
                                            'admin.forms.createOrganization.roleName',
                                        )}
                                        error={errors.roles?.[index]?.name}
                                        readOnly={readOnly}
                                        {...register(
                                            `roles.${index}.name` as const,
                                        )}
                                    />
                                </div>
                                <Controller
                                    control={control}
                                    name={`roles.${index}.groupIds`}
                                    render={({ field }) => (
                                        <MultiSelectList
                                            label={t(
                                                'admin.forms.createOrganization.roleGroups',
                                            )}
                                            options={groupOptions}
                                            value={field.value ?? []}
                                            onChange={values =>
                                                field.onChange(values)
                                            }
                                            disabled={
                                                readOnly || !groupOptions.length
                                            }
                                            error={normalizedGroupIdsError}
                                            placeholder={t(
                                                'search.genericPlaceholder',
                                            )}
                                            noOptionsLabel={t(
                                                'admin.forms.createOrganization.groupsEmpty',
                                            )}
                                            noMatchesLabel={t(
                                                'admin.forms.createOrganization.groupsEmpty',
                                            )}
                                            selectedLabel={t(
                                                'admin.forms.createOrganization.groupsEmpty',
                                            )}
                                        />
                                    )}
                                />
                                <Controller
                                    control={control}
                                    name={`roles.${index}.permissions`}
                                    render={({ field }) => (
                                        <MultiSelectList
                                            label={t(
                                                'admin.forms.createOrganization.rolePermissions',
                                            )}
                                            options={permissionOptions}
                                            value={field.value ?? []}
                                            onChange={values => {
                                                const normalizedValues = [
                                                    OrganizationRolePermission.readResidents,
                                                    ...values.filter(
                                                        permission =>
                                                            permission !==
                                                            OrganizationRolePermission.readResidents,
                                                    ),
                                                ];
                                                field.onChange(
                                                    normalizedValues,
                                                );
                                            }}
                                            disabled={readOnly}
                                            error={normalizedPermissionsError}
                                            placeholder={t(
                                                'search.genericPlaceholder',
                                            )}
                                            noOptionsLabel={t(
                                                'admin.forms.createOrganization.rolePermissionsEmpty',
                                            )}
                                            noMatchesLabel={t(
                                                'admin.forms.createOrganization.rolePermissionsEmpty',
                                            )}
                                            selectedLabel={t(
                                                'admin.forms.createOrganization.rolePermissionsEmpty',
                                            )}
                                        />
                                    )}
                                />
                                {!readOnly ? (
                                    <div>
                                        <button
                                            type="button"
                                            className="text-sm text-venetianRed underline"
                                            onClick={() => remove(index)}
                                        >
                                            {t(
                                                'admin.forms.createOrganization.removeRole',
                                            )}
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
